// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tuning.ts — every number the simulation obeys, in one place, so that the two things that must never
// silently disagree can be checked against each other.
//
// THIS FILE IS DELIBERATELY DUPLICATED. tests/helpers/audit.ts re-types every constant below as its
// own literal and never imports this module, because an audit that imports the game's own tables
// proves only that the game agrees with itself. A source-hygiene test fails CI on any import of
// ../src/tuning or ../src/rules from inside audit.ts, so a future well-meaning de-duplication cannot
// quietly turn the mechanism audit into a tautology. If you change a number here you must change it
// there, and the mechanism test is what tells you that you forgot.
//
// RULES_VERSION is a hash over the numbers a ghost's replay depends on. A shared route link carries it
// so a ghost recorded on an older build renders honestly as "recorded on an older build" instead of
// accusing an innocent player of tampering — see ghost.ts.

/** Every quantity in the simulation is an integer. There are no floats and no transcendentals. */

// ── the board ───────────────────────────────────────────────────────────────
// 8 wide so a cell is 375/8 = 46px at the reference phone width, and 10 tall rather than the 11 the
// design asked for, because 11 rows could not clear a 44px cell on a 375x667 SE under ANY arrangement
// of the chrome — and the row count is a rules constant, so it cannot adapt per viewport. It had to
// come out of the rules.
export const COLS = 8;
export const ROWS = 10;
export const CELLS = COLS * ROWS;

// ── the turn ────────────────────────────────────────────────────────────────
/** Slots per turn. Perception is evaluated at the END of every slot, so route SHAPE is the skill. */
export const SLOTS = 5;
/** Minders step after these slot indices (0-based), always. Chasing adds a third step after slot 3. */
export const MINDER_STEPS = [1, 4] as const;
export const CHASE_EXTRA_STEP = 3;

// ── verbs ───────────────────────────────────────────────────────────────────
export enum Verb {
  Hold = 0,
  Step = 1,
  Creep = 2,
  Bolt = 3,
  OpenDoor = 4,
  CloseDoor = 5,
  ForceDoor = 6,
  Breaker = 7,
  Grab = 8,
  Bank = 9,
  Slug = 10,
  Duct = 11,
}

/** Slot cost of each verb. Indexed by Verb. */
export const VERB_SLOTS: readonly number[] = [
  0, // Hold — costs nothing and is the only thing that cools the building
  1, // Step
  2, // Creep — half the ground for the same slots, which is why Brownout's clock bites
  1, // Bolt — two cells for one slot, and it leaves a scuff pointing where you went
  1, // OpenDoor
  1, // CloseDoor
  3, // ForceDoor
  2, // Breaker
  2, // Grab
  2, // Bank
  1, // Slug
  2, // Duct
];

/**
 * Din of each verb, before the floor type and the carry tax are added.
 *
 * A din costs NO HEAT. This is the single most important correction the design review produced: as
 * originally specified every verb but five added heat and the slug — the one tool sold to the player
 * as "din is a tool, not only a mistake" — cost up to 16 heat per use out of three uses. The throttle
 * only went up. Heat now comes exclusively from being perceived, from a mark being read, from a grab
 * and from forcing a door, so a slug is genuinely free and genuinely loud, and the summons it buys is
 * a real play rather than a fine.
 */
export const VERB_DIN: readonly number[] = [0, 0, 0, 4, 1, 0, 6, 2, 1, 0, 5, 1];

/** Bolt travels this many cells in a straight line. */
export const BOLT_CELLS = 2;
/** Slug range, and the minimum throw distance. */
export const SLUG_RANGE = 5;
/**
 * A real rule, in the real action table, shared by every mode. The tutorial's step 5 refuses a throw
 * at your own feet, and this is why that refusal is legal to assert: it is the game's rule, not a
 * tutorial-only invention. A step that invents its own legality is not driving the state machine.
 */
export const MIN_THROW = 4;
export const SLUGS_CARRIED = 3;

// ── floors ──────────────────────────────────────────────────────────────────
export enum Floor {
  Carpet = 0,
  Tile = 1,
  Grating = 2,
}
/** Added to the din of a movement verb. Floor type is drawn as TEXTURE, never hue. */
export const FLOOR_DIN: readonly number[] = [0, 1, 3];

/**
 * Carry tax, in DIN, added to every movement verb per item beyond the first.
 *
 * The design taxed carried loot in slots, which made carrying three or four items a strictly dead
 * branch and collapsed the mode's headline greed decision into a routing question. Taxing din instead
 * feeds the hearing system that already exists: greed makes you LOUD rather than slow, so the loop
 * closes as greed -> din -> summons -> perception -> heat.
 */
export const CARRY_DIN = 1;

// ── perception ──────────────────────────────────────────────────────────────
/** Arc half-angle in octant units (1 unit = 45 degrees). 1 = 45 deg, widened at SWEEP and above. */
export const ARC_OCTANTS = 1;
export const ARC_OCTANTS_WIDE = 2;
/** Base sight range by band. */
export const BASE_RANGE: readonly number[] = [4, 5, 5, 6];
/**
 * effRange = max(1, base - gloom - posture), in integers.
 *
 * Subtractive, not multiplicative. Under the design's multiplied factors a creeping intruder in a
 * gloom room had effRange floor(4 * 0.5 * 0.5) = 1 at EVERY band, i.e. was literally unseeable except
 * from an orthogonally adjacent cell — which made Brownout's optimum "toggle creep on turn 0 and never
 * toggle it off". Subtraction keeps gloom and creep each worth a real, readable two cells without
 * their product deleting the mode.
 */
export const GLOOM_PENALTY = 2;
export const CREEP_PENALTY = 2;
export const BOLT_BONUS = 2;
/** A lens is a fixed watchpost: wider, shorter, DEAF, and it rotates one octant per turn. */
export const LENS_RANGE = 4;
export const LENS_OCTANTS = 2;
export const LENS_PERIOD = 8;
/** Torch beam length in Brownout — the cells a minder forces back to bright in front of itself. */
export const TORCH_CELLS = 3;
/** Brownout only: the intruder's own knowledge radius. RENDER-ONLY; rules never branch on it. */
export const AWARENESS = 5;

// ── hearing ─────────────────────────────────────────────────────────────────
/** Sound BFS step costs. Walls block absolutely. */
export const SOUND_STEP = 1;
export const SOUND_CLOSED_DOOR = 3;
/**
 * A duct interior costs this much sound. NOT the design's 99: a duct that is acoustically infinite is
 * a cell where you cannot be perceived, cannot be heard, and — under the original decay rule — cooled
 * heat for free, so six turns of holding in one erased a full LOCKDOWN and the "you can talk the
 * building down" assertion would have gone green via a strategy nobody designed. A cost of 4 keeps a
 * duct quiet without making it a bunker, and NO_DECAY_IN_DUCT below closes the rest of the hole.
 */
export const SOUND_DUCT = 4;
/** Brownout is silent, so every din carries further. */
export const BROWNOUT_DIN_BONUS = 2;
/** The shout a minder makes on perceiving you — the loudest thing in the game, and it is about you. */
export const SHOUT_DIN = 8;

// ── heat ────────────────────────────────────────────────────────────────────
export enum Band {
  Calm = 0,
  Restless = 1,
  Sweep = 2,
  Lockdown = 3,
}
/** Lower bound of each band. A band, once touched, sets a RATCHET floor heat cannot fall below. */
export const BAND_FLOOR: readonly number[] = [0, 25, 50, 80];
export const HEAT_MAX = 100;

export const HEAT_PERCEIVED = 22;
export const HEAT_LENS = 14;
export const HEAT_READ = 8;
export const HEAT_GRAB = 6;
export const HEAT_FORCE = 10;

/** Cooling: this per unspent slot, plus a base, capped. */
export const DECAY_PER_SLOT = 2;
export const DECAY_BASE = 1;
export const DECAY_CAP = 9;
/**
 * Cooling is gated on no minder being within this sound-distance and none chasing.
 *
 * THIS IS THE NUMBER MOST LIKELY TO BE WRONG and it is load-bearing for the game's central promise, so
 * it is a named constant and tests/balance.test.ts sweeps it over {3,4,5} against the CAMP-AND-CASH
 * control arm rather than trusting it. The design proposed 5, which on an 80-cell floor is almost
 * never satisfied — the advertised central decision would not have been a decision on most turns.
 */
export const DECAY_GATE = 4;
/** No cooling inside a duct. You are absent from the building, not being quiet in it. */
export const NO_DECAY_IN_DUCT = true;

// ── marks ───────────────────────────────────────────────────────────────────
export enum Mark {
  Scuff = 0,
  OpenDoor = 1,
  GloomRoom = 2,
  MissingCase = 3,
  Slug = 4,
  ForcedDoor = 5,
}
/**
 * A mark is CONSUMED when it is read, once, ever.
 *
 * The design made MISSING CASE permanent and re-readable every turn, so after your second grab heat
 * climbed on its own regardless of anything you did: the dial the whole game is about was a timer.
 * Consuming every mark on read is what makes the past something you can outrun.
 */
export const SCUFF_LIFE = 6;
/** How far along its stored direction a read scuff sends a minder. */
export const SCUFF_FOLLOW = 3;
/** A missing case is only readable from within this range AND inside the arc. */
export const CASE_READ_RANGE = 2;
/** A minder reads at most this many marks per turn. */
export const READS_PER_TURN = 1;

// ── grabs ───────────────────────────────────────────────────────────────────
/**
 * A minder must be orthogonally adjacent and CHASING at the end of a turn before it may grab you on
 * the next one. That one turn of warning is the game's core fairness promise, and mechanism invariant
 * GRAB IS WARNED asserts it at zero tolerance — without it a mutation that grabs on contact passes
 * every other invariant in the file.
 */
export const GRABS_TO_LOSE = 2;

// ── the custodian ───────────────────────────────────────────────────────────
/** At LOCKDOWN he walks your uncollected loot back to the strongroom, this many cells a turn. */
export const CUSTODIAN_SPEED = 2;

// ── scoring ─────────────────────────────────────────────────────────────────
/**
 * score = banked * (1 + GHOST/100 + QUIET/100) + LEFT_BONUS * unusedTurns, and zero if you bank
 * nothing at all.
 *
 * MULTIPLICATIVE, and this was the worst finding of the entire design review. The flat version paid
 * GHOST 40 + QUIET 25 = 65 points for never being seen and never touching SWEEP — more than the three
 * most valuable items in the building combined — so the score-maximising line was a timid two-item
 * CALM run and the whole four-band escalation ladder was a scoring catastrophe to be avoided. It paid
 * the player not to play the game. Multiplying makes greed and stealth multiply instead of compete: a
 * bold five-item run is always worth chasing, and being clean about it is worth half as much again.
 */
export const GHOST_PCT = 50;
export const QUIET_PCT = 25;
/**
 * Unused turns are a PERCENTAGE bonus, capped — not a flat two points each.
 *
 * The flat version was the same mistake as the flat GHOST and QUIET bonuses, one layer down. Walking
 * out of a thirty-turn building on turn six paid forty-eight points for the twenty-four turns you did
 * not use, which is more than the two most valuable cases on the floor put together. The sim measured
 * exactly what you would expect: CAREFUL 84.7, BALANCED 79.7, GREEDY 69.1 — the difficulty tiers came
 * out in REVERSE order, because the game was paying its best score to the player who did the least.
 * As a capped percentage it rewards efficiency without ever outbidding the loot.
 */
export const TIME_PCT = 1;
export const TIME_CAP = 25;

// ── generation ──────────────────────────────────────────────────────────────
/** Loop lengths and the lens period are chosen so lcm <= 24: at CALM the world is periodic. */
export const LOOP_LENGTHS: readonly number[] = [4, 6, 8, 12];
export const WORLD_PERIOD = 24;
export const MIN_ROOMS = 5;
export const MAX_ROOMS = 7;
/** Extra doorways beyond the spanning tree, as a percentage. Loops are what make evasion possible. */
export const EXTRA_DOOR_PCT = 30;
export const MAX_REROLLS = 12;

/** Interest metrics, enforced per building with a reroll. Solvable is a floor, not a target. */
export const ARC_COVERAGE_MIN = 30;
export const ARC_COVERAGE_MAX = 70;
export const CYCLOMATIC_MIN = 2;
export const SLACK_MIN_PCT = 30;
export const SLACK_MAX_PCT = 85;
export const FORCED_RISK_MIN = 3;
/** A seed can otherwise be entirely carpet, which makes the movement half of hearing inert. */
export const NON_CARPET_MIN_PCT = 25;

/**
 * A stable fingerprint of everything a ghost's replay depends on. Bump by changing any number above;
 * ghost.ts stamps it into a shared link so an older recording is reported honestly rather than
 * rejected as tampered. Computed, not hand-maintained, because a hand-maintained version is a version
 * somebody forgets.
 */
export const RULES_VERSION: number = (() => {
  const parts = [
    SLOTS,
    COLS,
    ROWS,
    ...VERB_SLOTS,
    ...VERB_DIN,
    ...FLOOR_DIN,
    ...BASE_RANGE,
    ...BAND_FLOOR,
    GLOOM_PENALTY,
    CREEP_PENALTY,
    BOLT_BONUS,
    CARRY_DIN,
    SOUND_CLOSED_DOOR,
    SOUND_DUCT,
    HEAT_PERCEIVED,
    HEAT_LENS,
    HEAT_READ,
    HEAT_GRAB,
    HEAT_FORCE,
    DECAY_PER_SLOT,
    DECAY_BASE,
    DECAY_CAP,
    DECAY_GATE,
    SCUFF_LIFE,
    GHOST_PCT,
    QUIET_PCT,
    TIME_PCT,
    TIME_CAP,
    MIN_THROW,
    SLUG_RANGE,
    BOLT_CELLS,
  ];
  let h = 0x811c9dc5;
  for (const p of parts) {
    h ^= p & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
    h ^= (p >>> 8) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  // 12 bits is plenty to separate builds and keeps the shared link short.
  return h & 0xfff;
})();
